import {
	Autocomplete,
	Box,
	Checkbox,
	FormControlLabel,
	TextField,
	Typography,
} from "@mui/material";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

export const ConfirmForm = ({ form, order }) => {
	const [products, setProducts] = useState([]);
	const {
		control,
		watch,
		formState: { errors },
	} = form;
	useEffect(() => {
		setProducts(order?.lines);
	}, [order]);

	return (
		<Box my={1}>
			<Typography variant="h6">Products</Typography>

			<Box
				my={2}
				sx={{ display: "flex", flexDirection: "column", gap: 2 }}
			>
				{products.map((product, index) => {
					return (
						<Box
							key={product.id}
							sx={{
								p: 2,
								mx: 1,
								border: "1px solid",
								borderColor: "grey.400",
								borderRadius: 1,
							}}
						>
							{/* Product Title */}
							<Typography variant="body1" gutterBottom>
								{product.title}
							</Typography>
							<Box my={2}>
								<Controller
									name={`products.${index}.reference_number`}
									rules={{
										required: "Reference Number Required",
									}}
									control={control}
									defaultValue={null}
									render={({ field }) => (
										<TextField
											{...field}
											value={field.value ?? ""}
											fullWidth
											label="Reference Order Number"
											variant="outlined"
											error={
												!!errors?.products?.[index]
													?.reference_number
											}
											helperText={
												errors?.products?.[index]
													?.reference_number?.message
											}
										/>
									)}
								/>
							</Box>
							<Typography variant="body1">
								Expected Delivery Range
							</Typography>

							{/* Hidden Input for Product ID */}
							<Controller
								name={`products.${index}.id`}
								control={control}
								defaultValue={product.id}
								render={({ field }) => (
									<input
										type="hidden"
										{...field}
										value={field.value ?? ""}
									/>
								)}
							/>

							{/* Date Range Picker */}
							<Box my={2}>
								<LocalizationProvider
									dateAdapter={AdapterMoment}
								>
									<Box
										sx={{ display: "flex", gap: 2, mt: 2 }}
									>
										{/* Start Date */}
										<Controller
											name={`products.${index}.start_date`}
											control={control}
											rules={{
												required:
													"Start date is required",
											}}
											render={({ field }) => {
												const endDate = watch(
													`products.${index}.end_date`
												);
												return (
													<DatePicker
														label="Start Date"
														inputFormat="DD/MM/YYYY"
														minDate={moment()}
														maxDate={
															endDate
																? moment(
																		endDate
																  )
																: undefined
														}
														{...field}
														value={
															field.value ?? ""
														}
														renderInput={(
															params
														) => {
															return (
																<TextField
																	fullWidth
																	{...params}
																	error={
																		!!errors
																			?.products?.[
																			index
																		]
																			?.start_date
																	}
																	helperText={
																		errors
																			?.products?.[
																			index
																		]
																			?.start_date
																			?.message
																	}
																/>
															);
														}}
													/>
												);
											}}
										/>

										{/* End Date */}
										<Controller
											name={`products.${index}.end_date`}
											control={control}
											rules={{
												required:
													"End date is required",
											}}
											render={({ field }) => {
												const startDate = watch(
													`products.${index}.start_date`
												);
												return (
													<DatePicker
														label="End Date"
														inputFormat="DD/MM/YYYY"
														minDate={
															startDate ||
															moment()
														}
														{...field}
														value={
															field.value ?? ""
														}
														renderInput={(
															params
														) => (
															<TextField
																fullWidth
																{...params}
																error={
																	!!errors
																		?.products?.[
																		index
																	]?.end_date
																}
																helperText={
																	errors
																		?.products?.[
																		index
																	]?.end_date
																		?.message
																}
															/>
														)}
													/>
												);
											}}
										/>
									</Box>
								</LocalizationProvider>
							</Box>

							<Box mt={2}>
								<Box
									ml={1}
									sx={{
										display: "flex",
										gap: 2,
										alignItems: "center",
									}}
								>
									{/* Allow Return */}
									<Box sx={{ flex: 1 }}>
										<Controller
											name={`products.${index}.allow_return`}
											control={control}
											defaultValue={false}
											render={({ field }) => (
												<FormControlLabel
													control={
														<Checkbox
															{...field}
															checked={
																field.value
															}
														/>
													}
													label="Allow Return"
												/>
											)}
										/>
									</Box>

									{/* Return in Days */}
									<Box sx={{ flex: 1 }}>
										<Controller
											name={`products.${index}.return_in_days`}
											control={control}
											rules={{
												validate: (value) => {
													const allowReturn = watch(
														`products.${index}.allow_return`
													);
													if (
														allowReturn &&
														(!value ||
															Number(value) < 1)
													) {
														return "Number of days is required";
													}
													return true;
												},
											}}
											render={({ field }) => {
												const options = [7, 15, 30];
												return (
													<Autocomplete
														freeSolo
														options={options.map(
															(o) => o.toString()
														)}
														value={
															field.value?.toString() ??
															""
														}
														onChange={(
															_,
															newValue
														) => {
															const num =
																Number(
																	newValue
																);
															field.onChange(
																isNaN(num)
																	? ""
																	: num
															);
														}}
														onInputChange={(
															_,
															inputValue
														) => {
															const num =
																Number(
																	inputValue
																);
															field.onChange(
																isNaN(num)
																	? ""
																	: num
															);
														}}
														renderInput={(
															params
														) => (
															<TextField
																{...params}
																fullWidth
																label="Return in Days"
																variant="outlined"
																error={
																	!!errors
																		?.products?.[
																		index
																	]
																		?.return_in_days
																}
																helperText={
																	errors
																		?.products?.[
																		index
																	]
																		?.return_in_days
																		?.message
																}
															/>
														)}
													/>
												);
											}}
										/>
									</Box>
								</Box>
							</Box>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};
